import { useEffect } from 'react';
import { Close, Shield } from '@mui/icons-material';
import { ComplianceItem } from '../../data/types';

type ComplianceLegalDrawerProps = {
  item: ComplianceItem;
  region: string;
  onClose: () => void;
};

/** Side drawer with legal context, agency lanes, and references for a compliance item. */
export const ComplianceLegalDrawer = ({
  item,
  region,
  onClose,
}: ComplianceLegalDrawerProps) => {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
      <button
        type="button"
        className="absolute inset-0 bg-white/70 backdrop-blur-[2px]"
        onClick={onClose}
        aria-label="Close legal information"
      />

      <aside className="relative flex h-full w-full max-w-[480px] flex-col bg-white shadow-[-12px_0_48px_rgba(1,56,71,0.12)]">
        <div className="flex items-start justify-between border-b border-[#E9E9E9] px-6 py-5">
          <div className="flex gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#EFF8FA]">
              <Shield className="text-primary-400" />
            </span>
            <div>
              <h2 className="font-raleway text-xl font-bold text-[#111111]">{item.title}</h2>
              <p className="mt-1 font-open-sans text-sm text-[#808080]">Why this requirement matters</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-[#737373] transition-colors hover:bg-[#f8f8f8]"
            aria-label="Close"
          >
            <Close />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          <section>
            <h3 className="font-raleway text-sm font-semibold text-[#4D4D4D]">HomeCook summary</h3>
            <p className="mt-2 font-open-sans text-sm leading-relaxed text-[#4D4D4D]">
              {item.legal.summary}
            </p>
            {item.legal.why_required && item.legal.why_required !== item.legal.summary && (
              <p className="mt-3 font-open-sans text-sm leading-relaxed text-[#4D4D4D]">
                {item.legal.why_required}
              </p>
            )}
          </section>

          {item.legal.agency && (
            <section className="mt-5 rounded-xl border border-[#E9E9E9] bg-[#fafafa] p-4">
              <h3 className="font-raleway text-sm font-semibold text-[#4D4D4D]">Agency lane</h3>
              <p className="mt-2 font-open-sans text-sm text-[#013847]">{item.legal.agency}</p>
              {item.legal.agency_lane && (
                <p className="mt-1 font-open-sans text-xs uppercase tracking-wide text-[#808080]">
                  {item.legal.agency_lane.replace(/_/g, ' ')}
                </p>
              )}
            </section>
          )}

          <section className="mt-6 rounded-xl border border-[#5DB6CE]/20 bg-[#EFF8FA]/60 p-4">
            <h3 className="font-raleway text-sm font-semibold text-[#013847]">
              Location note - {region || 'your area'}
            </h3>
            <p className="mt-2 font-open-sans text-sm leading-relaxed text-[#013847]/80">
              {item.legal.regionNote}
            </p>
          </section>

          {item.legal.references?.length > 0 && (
            <section className="mt-6">
              <h3 className="font-raleway text-sm font-semibold text-[#4D4D4D]">Legal references</h3>
              <ul className="mt-3 space-y-3">
                {item.legal.references.map((reference) => (
                  <li
                    key={`${reference.label}-${reference.citation}`}
                    className="rounded-xl border border-[#E9E9E9] bg-[#fafafa] p-4"
                  >
                    <p className="font-raleway text-sm font-semibold text-[#013847]">
                      {reference.label}
                    </p>
                    <p className="mt-1 font-open-sans text-sm leading-relaxed text-[#4D4D4D]">
                      {reference.citation}
                    </p>
                    {reference.url && (
                      <a
                        href={reference.url}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-2 inline-block font-open-sans text-sm text-primary-200 underline"
                      >
                        View source
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {item.legal.disclaimer && (
            <p className="mt-6 font-open-sans text-xs leading-relaxed text-[#808080]">
              {item.legal.disclaimer}
            </p>
          )}
        </div>
      </aside>
    </div>
  );
};

export default ComplianceLegalDrawer;
